// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {AuthErrorState} from '@app/features/auth/flow/AuthErrorState';
import {AuthLoadingState} from '@app/features/auth/flow/AuthLoadingState';
import {InviteHeader} from '@app/features/auth/flow/InviteHeader';
import * as InviteCommands from '@app/features/invite/commands/InviteCommands';
import styles from '@app/features/invite/components/modals/InviteAcceptModal.module.css';
import Invites from '@app/features/invite/state/Invites';
import {
	isGroupDmInvite,
	isGuildInvite,
	isPackInvite as isPackInviteGuard,
} from '@app/features/invite/types/InviteTypes';
import {getGroupDmInviteCounts} from '@app/features/invite/utils/GroupDmInviteCounts';
import {
	GuildInvitePrimaryAction,
	getGuildInviteActionState,
	getGuildInvitePrimaryAction,
	isGuildInviteActionDisabled,
} from '@app/features/invite/utils/GuildInviteActionState';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import foodPatternUrl from '@app/media/images/i-like-food.svg';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';

const logger = new Logger('InviteAcceptModal');
interface InviteAcceptModalProps {
	code: string;
}
export const InviteAcceptModal = observer(function InviteAcceptModal({code}: InviteAcceptModalProps) {
	const {t, i18n} = useLingui();
	const inviteState = Invites.invites.get(code) ?? null;
	const invite = inviteState?.data ?? null;
	const [isAccepting, setIsAccepting] = useState(false);
	useEffect(() => {
		if (!inviteState) {
			void InviteCommands.fetchWithCoalescing(code).catch(() => {});
		}
	}, [code, inviteState]);
	const isGroupDM = invite != null && isGroupDmInvite(invite);
	const groupDMCounts =
		invite && isGroupDM
			? getGroupDmInviteCounts({
					channelId: invite.channel.id,
					inviteMemberCount: invite.member_count,
				})
			: null;
	const isPackInvite = invite != null && isPackInviteGuard(invite);
	const guildActionState = getGuildInviteActionState({invite});
	const {presenceCount, memberCount} = guildActionState;
	const inviteForHeader = useMemo(() => {
		if (!invite) return null;
		if (isGroupDM && groupDMCounts) {
			return {
				...invite,
				member_count: groupDMCounts.memberCount,
			};
		}
		return {
			...invite,
			presence_count: presenceCount,
			member_count: memberCount,
		};
	}, [invite, isGroupDM, presenceCount, memberCount, groupDMCounts?.memberCount]);
	const splashUrl = useMemo(() => {
		if (!invite || !isGuildInvite(invite)) {
			return null;
		}
		const guild = invite.guild;
		if (!guild.id || !guild.splash) {
			return null;
		}
		return AvatarUtils.getGuildSplashURL(
			{
				id: guild.id,
				splash: guild.splash,
			},
			4096,
		);
	}, [invite]);
	const isJoinDisabled = isGuildInviteActionDisabled(guildActionState);
	const primaryActionType = getGuildInvitePrimaryAction(guildActionState);
	const primaryLabel = useMemo(() => {
		if (isGroupDM) return t`Join Group DM`;
		switch (primaryActionType) {
			case GuildInvitePrimaryAction.InvitesDisabled:
				return t`Invites Disabled`;
			case GuildInvitePrimaryAction.GoToCommunity:
				return t`Go to Community`;
			default:
				return t`Join Community`;
		}
	}, [isGroupDM, primaryActionType]);
	const handleDismiss = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleAccept = useCallback(async () => {
		setIsAccepting(true);
		try {
			await InviteCommands.acceptAndTransitionToChannel(code, i18n);
			ModalCommands.pop();
		} catch (error) {
			logger.error(' Failed to accept invite:', error);
			setIsAccepting(false);
		}
	}, [code]);
	const renderBody = () => {
		if (!inviteState || inviteState.loading) {
			return (
				<div className={styles.stateHost}>
					<AuthLoadingState />
				</div>
			);
		}
		if (inviteState.error || !inviteState.data || !inviteForHeader) {
			return (
				<div className={styles.stateHost}>
					<AuthErrorState
						title={<Trans>Invite not found</Trans>}
						text={<Trans>This invite may have expired or been deleted.</Trans>}
					/>
				</div>
			);
		}
		if (isPackInvite && invite) {
			const packKindLabel = invite.pack.type === 'emoji' ? t`Emoji pack` : t`Sticker pack`;
			const packActionLabel = invite.pack.type === 'emoji' ? t`Install Emoji Pack` : t`Install Sticker Pack`;
			return (
				<div className={styles.cardInner}>
					<InviteHeader invite={inviteForHeader} />
					<p className={styles.packDescriptionText}>{invite.pack.description || t`No description provided.`}</p>
					<div className={styles.packMetaRow}>
						<span className={styles.packMetaText}>{packKindLabel}</span>
						<span className={styles.packMetaText}>
							<Trans>Created by {invite.pack.creator.username}</Trans>
						</span>
						{invite.inviter ? (
							<span className={styles.packMetaText}>
								<Trans>
									Invited by {invite.inviter.username}#{invite.inviter.discriminator}
								</Trans>
							</span>
						) : null}
					</div>
					<p className={styles.packNote}>
						<Trans>Accepting this invite installs the pack automatically.</Trans>
					</p>
					<div className={styles.actions}>
						<Button onClick={handleAccept} disabled={isAccepting} submitting={isAccepting}>
							{packActionLabel}
						</Button>
					</div>
				</div>
			);
		}
		return (
			<div className={styles.cardInner}>
				<InviteHeader invite={inviteForHeader} />
				{isJoinDisabled ? (
					<p className={styles.disabledText}>
						{guildActionState.isRaidDetected ? (
							<Trans>
								Fluxer detected a potential raid in this community. Invites are temporarily disabled, so new users can't
								join right now.
							</Trans>
						) : (
							<Trans>This community has temporarily disabled invites. You can try again later.</Trans>
						)}
					</p>
				) : null}
				<div className={styles.actions}>
					<Button onClick={handleAccept} disabled={isAccepting || isJoinDisabled} submitting={isAccepting}>
						{primaryLabel}
					</Button>
				</div>
			</div>
		);
	};
	return (
		<Modal.Root size="large" className={styles.root} centered onClose={handleDismiss}>
			<Modal.ScreenReaderLabel text={t`Accept Invite`} />
			<Modal.InsetCloseButton onClick={handleDismiss} disabled={isAccepting} />
			<div className={styles.background} aria-hidden>
				{splashUrl ? (
					<div className={styles.splashImage} style={{backgroundImage: `url(${splashUrl})`}} />
				) : (
					<div className={styles.patternImage} style={{backgroundImage: `url(${foodPatternUrl})`}} />
				)}
			</div>
			<div className={styles.cardHost}>
				<div className={styles.card}>{renderBody()}</div>
			</div>
		</Modal.Root>
	);
});
